<template>
  <div class="flex h-full min-w-0 flex-col">
    <!-- Coverage tabs -->
    <div class="flex items-stretch border-b border-border">
      <SButton
        type="button"
        variant="ghost"
        size="lg"
        :class="[
          'discount-quantity-btn rounded-none border-b-2 border-transparent -mb-px hover:bg-transparent',
          discountInfo.type === 1 ? 'flex-1' : 'w-full',
          discountInfo.isOnAllOrder
            ? '!border-primary !text-primary'
            : '!text-muted-foreground',
        ]"
        @click="makeDiscountOnAllOrder"
      >
        {{ $t("discount.all-order") }}
      </SButton>
      <SButton
        v-if="discountInfo.type === 1"
        type="button"
        variant="ghost"
        size="lg"
        :class="[
          'discount-quantity-btn flex-1 rounded-none border-b-2 border-transparent -mb-px hover:bg-transparent',
          !discountInfo.isOnAllOrder
            ? '!border-primary !text-primary'
            : '!text-muted-foreground',
        ]"
        @click="UpdateDiscountApplicationCoverage"
      >
        {{ $t("discount.specific-tickets") }}
      </SButton>
    </div>

    <!-- Discount type + values -->
    <div class="flex min-w-0 flex-col gap-5 py-6 md:flex-row md:gap-8">
      <div class="flex w-full shrink-0 flex-col gap-3 md:w-52">
        <SButton
          type="button"
          class="discount-type-btn w-full"
          :variant="discountInfo.type === 1 ? 'default' : 'outline'"
          @click="updateDiscountType(DiscountTypes.Percentage_Discount)"
        >
          {{ $t("discount.choose-percentage").toUpperCase() }}
        </SButton>
        <SButton
          type="button"
          class="discount-type-btn w-full"
          :variant="discountInfo.type === 2 ? 'default' : 'outline'"
          @click="updateDiscountType(DiscountTypes.Fixed_Value_Discount)"
        >
          {{ $t("discount.choose-fixed-value").toUpperCase() }}
        </SButton>
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-3">
        <div class="grid grid-cols-3 gap-3">
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 5 ? 'default' : 'outline'"
            @click="updateDiscountValue(5)"
            data-cy="discount-5"
          >
            {{ discountInfo.type === 1 ? "5%" : currency + " 5" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 10 ? 'default' : 'outline'"
            @click="updateDiscountValue(10)"
            data-cy="discount-10"
          >
            {{ discountInfo.type === 1 ? "10%" : currency + " 10" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 15 ? 'default' : 'outline'"
            @click="updateDiscountValue(15)"
          >
            {{ discountInfo.type === 1 ? "15%" : currency + " 15" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 30 ? 'default' : 'outline'"
            @click="updateDiscountValue(30)"
            data-cy="discount-30"
          >
            {{ discountInfo.type === 1 ? "30%" : currency + " 30" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 50 ? 'default' : 'outline'"
            @click="updateDiscountValue(50)"
            data-cy="discount-50"
          >
            {{ discountInfo.type === 1 ? "50%" : currency + " 50" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 100 ? 'default' : 'outline'"
            @click="updateDiscountValue(100)"
            data-cy="discount-100"
          >
            {{ discountInfo.type === 1 ? "100%" : currency + " 100" }}
          </SButton>
        </div>
        <SButton
          type="button"
          variant="outline"
          size="icon"
          class="h-[52px] w-full"
          aria-label="open calculator"
          @click="showCalculator"
        >
          <SvgIcon
            :iconContent="calcIcon"
            :iconAlt="calcAlt"
            :iconStyle="calcIconStyle"
          />
        </SButton>
      </div>
    </div>

    <!-- Summary + actions -->
    <div class="mt-auto">
      <SSeparator />
      <div
        class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pt-4"
      >
        <div
          class="grid grid-cols-[auto_auto_minmax(3.5rem,auto)] gap-x-4 gap-y-1 text-sm"
        >
          <span class="font-medium">{{ $t("button.discount") }}</span>
          <span class="text-muted-foreground">{{ currency }}</span>
          <span class="text-right font-semibold tabular-nums">
            {{ (discountResult / 100).toFixed(2) }}
          </span>
          <span class="font-medium">
            {{ $t("completeorder.order-total") }}
          </span>
          <span class="text-muted-foreground">{{ currency }}</span>
          <span class="text-right font-semibold tabular-nums">{{ total }}</span>
        </div>
        <div class="flex gap-2">
          <SButton
            type="button"
            class="h-12 min-w-[110px]"
            @click="applyDiscount2"
            data-cy="apply-discount-button"
          >
            {{ $t("promocode.apply") }}
          </SButton>
          <SButton
            type="button"
            class="h-12 min-w-[110px]"
            :variant="isDiscountApplied ? 'destructive' : 'outline'"
            :disabled="!isDiscountApplied"
            @click="refreshDiscount"
            data-cy="remove-discount-button"
          >
            {{ $t("common.remove") }}
          </SButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import {
  BookingStatus,
  IProductSlotState,
  PaymentStatus,
} from "@/types/db/product-slot-state";
import BookingModule from "@/store/modules/booking";
import {
  DiscountType,
  DiscountTypes,
  IBookingsSteps,
  IPSSDiscount,
} from "@/interfaces/store/IBooking";
import { BookingStep } from "@/constants/booking";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import {
  initiateDiscount,
  refreshDiscount,
} from "@/utils/vue-helpers/discount";
import {
  pssUnpaidValue,
  restToPay,
  createGenericDiscountAccountings,
  isPSSPaid,
  removeGenericDiscountAccountings,
  setAllPaymentCodePendingAccountingsAsCancelled,
  setAllDiscountCodePendingAccountingsAsCancelled,
  createDiscountCodeAccounting,
  createPaymentCodeAccounting,
  excludedTaxByPss,
} from "@/utils/vue-helpers/new-accountings";
import calculatorSvg from "@/assets/images/svg/calculator-sign.svg";
import dayjs from "dayjs";
import {
  AccountingState,
  IAccounting,
  PromocodeCampaignType,
} from "@/types/db/accounting";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { cloneDeep } from "lodash";
import { LogService } from "@/services/logging";
import { Currencies } from "@/constants/currency";
import { roundToNearestMultipleOf5, sumArray } from "@/utils/helpers/common";
import { getPSSValue } from "@/utils/helpers/product-slot-state";
import { isBookingInDexie } from "@/utils/helpers/booking";
import AppModule from "@/store/modules/app";
import { SButton, SSeparator } from "@smeetz/ui-core";

interface IDiscountInfo {
  type: number;
  value: number;
  isOnAllOrder: boolean;
  pssList: IProductSlotState[];
  bookingId: string;
}

export default defineComponent({
  name: "app",
  components: { SButton, SSeparator },
  setup() {
    const calcIcon = calculatorSvg;
    const calcAlt = "calculator";
    const calcIconStyle = "width: 30px; height: 30px;";

    const { t } = useI18n({});
    const predefinedDiscounts: { values: number[]; percentages: number[] } = {
      values: [],
      percentages: [10, 20, 50, 100],
    };

    const discountInfo = computed(() => {
      return {
        type: BookingModule.selectedBookingDiscountTrack?.type,
        value: BookingModule.selectedBookingDiscountTrack?.value,
        isOnAllOrder: BookingModule.selectedBookingDiscountTrack?.isOnAllOrder,
        pssList: [],
        bookingId: BookingModule.selectedBookingUUID,
      } as IDiscountInfo;
    });

    const currency = computed(() => {
      return BookingModule.selectedBooking?.currency;
    });

    const isDiscountApplied = computed(() => {
      if (
        BookingModule.selectedBookingDiscountTrack &&
        BookingModule.selectedBookingDiscountTrack.isApplied
      ) {
        return true;
      }
      return false;
    });

    const discountResult = computed(() => {
      // Returns the amount of the discount:
      // 1- If the discount type is percentage X, then discountAmount = concerned prices Amount * X / 100
      // 2- If the discount type is a value X chf, the discountAmount = X * 100 (because it is in cents)
      let discount = 0;
      if (discountInfo.value.type === 1) {
        let sum = 0;
        if (BookingModule.selectedBooking) {
          // Only prices concerned bu the discount will be considerd when calculating the sum of the order
          const discountPrices =
            BookingModule.selectedBookingDiscountTrack?.pricesIds;

          if (!discountPrices || discountPrices.length === 0) {
            // even though the type of this discount is to be applied on specific tickets yet we have no tickets concerned about it, lets return 0
            return 0;
          }

          // lets calculate the sum of the pss values of this booking (but remember! we already filtered prices that are not concerned by this discount)
          BookingModule.selectedBooking?.pssList
            .filter(
              (pss) =>
                discountPrices?.findIndex(
                  (priceId) => priceId === pss.priceId
                ) > -1 &&
                !isPSSPaid(pss) &&
                pss.paymentStatus !== PaymentStatus.Completed &&
                pss.bookingStatus !== BookingStatus.Canceled &&
                pss.bookingStatus !== BookingStatus.Abandoned
            )
            .map((pss) => getPSSValue(pss))
            .forEach((pssValue) => (sum = sum + pssValue));

          discount = (discountInfo.value.value * sum) / 100;
        }
      } else if (discountInfo.value.type === 2) {
        discount = discountInfo.value.value * 100; // converted to cents
      }
      if (currency.value === Currencies.CHF) {
        // Round discount value to a multiple of 5 for CHF transactions
        discount = roundToNearestMultipleOf5(discount);
      }
      return discount;
    });

    const bookingTotalAmount = computed(() => {
      let sum = 0;
      BookingModule.selectedBooking?.pssList
        .map((pss) => getPSSValue(pss))
        .forEach((pssValue) => (sum = sum + pssValue));
      return sum;
    });

    function updateDiscountType(typeValue: DiscountType) {
      // Update type to whether value discount or percentage discount

      // Do nothing if it is the same discount type
      const currentDiscountType =
        BookingModule.selectedBookingDiscountTrack?.type;
      if (currentDiscountType === typeValue) {
        return;
      }
      BookingModule.updateDiscountType({
        BookingUUID: BookingModule.selectedBookingUUID,
        type: typeValue,
      });
      // Since we updated discount type lets erase the previous discount value to a default of 10
      BookingModule.updateDiscountValue({
        BookingUUID: BookingModule.selectedBookingUUID,
        value: 5,
      });
    }

    function updateDiscountValue(val: number) {
      // Update the discount raw value (e.g: 20%, 50 CHF ... )
      BookingModule.updateDiscountValue({
        BookingUUID: BookingModule.selectedBookingUUID,
        value: val,
      });
      // discountInfo.value.value = val;
    }

    function UpdateDiscountApplicationCoverage() {
      ModalModule.setStatus(ModalStatus.DiscountSelectTickets);
    }

    function makeDiscountOnAllOrder() {
      BookingModule.updateDiscountCoverage({
        BookingUUID: BookingModule.selectedBookingUUID,
        isOnAllOrder: true,
      });

      BookingModule.updateDiscountPrices({
        BookingUUID: BookingModule.selectedBookingUUID,
        pricesIds: [
          ...new Set(
            BookingModule.selectedBooking?.pssList.map((pss) => pss.priceId)
          ),
        ],
      });
      discountInfo.value.pssList = BookingModule.selectedBooking?.pssList || [];
    }

    function showCalculator() {
      ModalModule.setStatus(ModalStatus.PaymentCalculator);
    }

    async function applyDiscount2() {
      LogService.info(
        `User clicked to apply generic discount, uuid:${BookingModule.selectedBooking?.uuid}`
      );
      // Show spinner
      AppModule.updatedIsLoading(true);
      // Apply the discount using the current info stored in discountInfo object
      // 1- calculate discount of each pss using a percentage
      // (this percentage is the same as user has entered if he chose percentage discount)
      // (if user chose value discount, then we should calculate the percentage ourselves)
      // In this point and to have correct values of pss.priceValue before applying the discount, keep
      // in mind that the pss can have a promocode applied in BookingModule.promocode. So consider it as
      // pss.priceValue -  promocodeDiscount
      // 2- if booking has accounting, remove it ... keep in mind that a booking that had discount and had accounting is now having fake pss.priceValue
      // 3- call createPosAccounting

      const bookingCopy1 = cloneDeep(BookingModule.selectedBooking);
      if (!bookingCopy1) {
        throw new Error(PSBErrors.NoBookingInModule);
      }

      // Before applying a new generic discount we remove any pending generic discount accounting that the user has previously created
      console.log("----------- calling removeGenericDiscountAccountings");
      removeGenericDiscountAccountings(bookingCopy1);
      // TODO Hamza: If we have applied codes for this booking, then we remove their accountings
      //     - Remove pending payment code accountings of all applied payment codes
      // removeAllPaymentCodePendingAccountings(bookingCopy1);
      // We do not remove codes accountings from booking object because we may have the booking
      // already synched in a pending state and if we now remove its code accountings it will not be PUT to completed
      // but it will be POSTed as pending again and then POSTed as completed but here POST will not have the effect
      // to complete what was pending
      // Did we have such a scenario? Yes, a booking with code then Adyen payment but cancelled (now the booking is synched as pending) and then user apply 100% generic discount
      console.log(
        "----------- calling setAllPaymentCodePendingAccountingsAsCancelled"
      );
      setAllPaymentCodePendingAccountingsAsCancelled(bookingCopy1);
      //     - Remove pending discount code accountings of all applied discount codes
      // removeAllDiscountCodePendingAccountings(bookingCopy1);
      // We do not remove codes accountings from booking object because we may have the booking
      // already synched in a pending state and if we now remove its code accountings it will not be PUT to completed
      // but it will be POSTed as pending again and then POSTed as completed but here POST will not have the effect
      // to complete what was pending
      // Did we have such a scenario? Yes, a booking with code then Adyen payment but cancelled (now the booking is synched as pending) and then user apply 100% generic discount
      console.log(
        "----------- calling setAllDiscountCodePendingAccountingsAsCancelled"
      );
      setAllDiscountCodePendingAccountingsAsCancelled(bookingCopy1);

      // Now lets update the booking in the state before appyling the discount, and before applying any code
      console.log("----------- calling BookingModule.updateBooking");
      BookingModule.updateBooking(bookingCopy1);

      // Lets make another copy of the booking from the state, because if we keep using the obj bookingCopy1 and then update it again in the state restToPay will not react and balance shown in UI will stay the bookingTotal
      const bookingCopy = cloneDeep(BookingModule.selectedBooking);

      if (!bookingCopy) {
        throw new Error(PSBErrors.NoBookingInModule);
      }

      let bookingPSS = bookingCopy.pssList;
      if (!bookingPSS || !bookingPSS.length) {
        return;
      }
      const isEditOrder = await isBookingInDexie(bookingCopy.uuid);
      if (isEditOrder) {
        // We're in EditOrder case, we don't apply discount on paid pss. Lets build an array of not completed pss
        bookingPSS = [];
        bookingCopy.pssList.forEach((pss) => {
          if (!isPSSPaid(pss)) {
            bookingPSS.push(pss);
          }
        });
      }

      let discountPercentage = 0;

      /* BEGIN OF NEW LOGIC */
      // in the new logic:
      // 1- we know there is no specific tickets discount for fixed amount discount
      // 2- fixed amount discount is not going to be distributed on pss by generating a percentage, no, it is going to be distributed
      // on pss list pss by pss until the fixed discount amount is 0
      // We're doing this to avoid having decimals with cents in pss.pricevalue/pss._discountValue/accountingEntry.value

      const discountPrices =
        BookingModule.selectedBookingDiscountTrack?.pricesIds;

      const pssDiscounts: IPSSDiscount[] = [];

      if (discountInfo.value.type === 1) {
        discountPercentage = discountInfo.value.value;
        let totalDiscountApplied = 0;
        let maxDiscountValueReached = false;
        const notCanceledPssList = bookingCopy.pssList.filter(
          (pss) =>
            pss.bookingStatus !== BookingStatus.Canceled &&
            pss.bookingStatus !== BookingStatus.Abandoned
        );
        const sumOfNotCanceledPSSValues = sumArray(
          notCanceledPssList.map((pss) => getPSSValue(pss))
        );
        const amountPaid = BookingModule.amountPaid;
        for (const pss of bookingPSS) {
          // If pss is already paid or canceled or abandoned then we don't apply discount on it
          if (
            isPSSPaid(pss) ||
            pss.paymentStatus === PaymentStatus.Completed ||
            pss.bookingStatus === BookingStatus.Canceled ||
            pss.bookingStatus === BookingStatus.Abandoned
          ) {
            continue;
          }
          if (!discountPrices) {
            // So current pss is not considered in the discount
            pss._discountValue = 0;
            continue;
          }
          if (
            discountPrices.findIndex((priceId) => priceId === pss.priceId) > -1
          ) {
            // So current pss is considered in this discount
            // we do not apply discounts to the excluded tax value of a pss
            let discountValue = Math.round(
              ((pssUnpaidValue(pss) -
                excludedTaxByPss.value(String(pss.uuid))) *
                discountPercentage) /
                100
            );
            totalDiscountApplied += discountValue;
            if (
              totalDiscountApplied >=
              sumOfNotCanceledPSSValues - amountPaid
            ) {
              // we do not apply a discount value greater then the amount left to pay
              maxDiscountValueReached = true;
              discountValue = Math.min(
                sumOfNotCanceledPSSValues - amountPaid,
                discountValue
              );
            }
            pss._discountValue = discountValue;

            const applyTime = dayjs().toISOString();
            pssDiscounts.push({
              pssId: pss._id,
              pssUUID: pss.uuid,
              pssOriginalPriceValue: pssUnpaidValue(pss),
              pssNewPriceValue: pssUnpaidValue(pss) - pss._discountValue,
              priceId: pss.priceId,
              categoryId: pss.categoryId,
              applyTime: applyTime,
            });
            if (maxDiscountValueReached) {
              break;
            }
          } else pss._discountValue = 0;
        }
      } else if (discountInfo.value.type === 2) {
        // now lets loop thru pss and make them fully discounted until we reach the discount amount decided by user

        const discountFixedAmount = discountInfo.value.value * 100;
        let remainingDiscountAmount = discountFixedAmount;
        let pssDiscountValueSum = 0;
        // Skip the CHF rounding when the discount covers the full remaining price:
        // rounding it would leave a 0.01/0.02 balance that can never be paid (L3-7899)
        let allDiscountedPssFullySettled = true;
        for (let i = 0; i < bookingPSS.length; i++) {
          const pss = bookingPSS[i];
          // If pss is already paid or canceled or abandoned then we don't apply discount on it
          if (
            isPSSPaid(pss) ||
            pss.paymentStatus === PaymentStatus.Completed ||
            pss.bookingStatus === BookingStatus.Canceled ||
            pss.bookingStatus === BookingStatus.Abandoned
          ) {
            continue;
          }
          if (pssUnpaidValue(pss) <= remainingDiscountAmount) {
            // lets make this pss fully discounted
            pss._discountValue = pssUnpaidValue(pss);
            // decrease remainingDiscountAmount
            remainingDiscountAmount =
              remainingDiscountAmount - pssUnpaidValue(pss);
          } else {
            // so here (pss price value - pssPromocodeDiscount) is greater than what we still have as a discount amount to use
            // we will make a normal discount, not a full one
            pss._discountValue = remainingDiscountAmount;
            remainingDiscountAmount = 0;
            allDiscountedPssFullySettled = false;
          }
          pssDiscountValueSum += pss._discountValue;

          if (
            i === bookingPSS.length - 1 &&
            currency.value === Currencies.CHF &&
            !allDiscountedPssFullySettled
          ) {
            // Change the last pss discount value to a have a total sum of discounts be a multiple of 5 for CHF transactions
            // as CHF doesn't have coins below 5 cents

            // this is the sum of discounts we're looking for
            const roundedDiscountValueSum =
              roundToNearestMultipleOf5(pssDiscountValueSum);

            // calculate the difference between what we want and what we have
            const difference = roundedDiscountValueSum - pssDiscountValueSum;

            // add the difference to the last pss discount value
            pss._discountValue += difference;
          }
          const applyTime = dayjs().toISOString();
          pssDiscounts.push({
            pssId: pss._id,
            priceId: pss.priceId,
            pssUUID: pss.uuid,
            pssOriginalPriceValue: pssUnpaidValue(pss),
            pssNewPriceValue: pssUnpaidValue(pss) - pss._discountValue,
            categoryId: pss.categoryId,
            applyTime: applyTime,
          });
        }
      }
      /* END OF NEW LOGIC */

      // Add tracking of this discount
      // Now update the discount as isApplied: true and add the pssDiscounts array
      BookingModule.applyDiscount({
        BookingUUID: BookingModule.selectedBookingUUID,
        pssDiscounts: pssDiscounts,
      });

      // Now we need to create genericDiscountAccountings
      // removeGenericDiscountAccountings(bookingCopy);
      createGenericDiscountAccountings(bookingCopy);
      // BookingModule.updateBooking(bookingCopy);
      // TODO Hamza: Recreate accountings for applied discount codes and then payment codes
      //     - Create discount code accountings of all applied discount codes
      // lets get all applied discount codes and sort them by applyDateTime
      const sortedAppliedDiscountCodes = BookingModule.appliedPromocodes.filter(
        (p) =>
          p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER &&
          p.promocode.promoCodeType !==
            PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
          p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD &&
          p.promocode.promoCodeType !==
            PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
      ); /*.sort((p) => p.createdAt)*/
      for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
        createDiscountCodeAccounting(
          bookingCopy,
          sortedAppliedDiscountCodes[i]
        );

        // If there is no accounting created for this code (may be booking is already full discounted) then lets remove it from BookingModule.promocodes[]
        const stillValidAccountings = bookingCopy.accountings.filter(
          (acc) =>
            (acc.promoCodeUuid ===
              sortedAppliedDiscountCodes[i].promocode.code ||
              acc.promocode === sortedAppliedDiscountCodes[i].promocode.code) &&
            (acc.state === AccountingState.Pending ||
              acc.state === AccountingState.Completed)
        );
        if (stillValidAccountings && stillValidAccountings.length > 0) {
          const accEntries = stillValidAccountings
            .map((acc) => acc.accountingEntriesList)
            .flat();
          if (accEntries && accEntries.length > 0) {
            // So we have a valid accounting for this code, lets not remove it
            continue;
          } else {
            // So we have no valid accounting for this code, lets remove it from BookingModule.promocodes
            BookingModule.removePromocode({
              bookingUuid: String(bookingCopy.uuid),
              promocodeUuid: sortedAppliedDiscountCodes[i].promocode.code,
            });
          }
        } else {
          // So we have no valid accounting for this code, lets remove it from BookingModule.promocodes
          BookingModule.removePromocode({
            bookingUuid: String(bookingCopy.uuid),
            promocodeUuid: sortedAppliedDiscountCodes[i].promocode.code,
          });
        }
      }
      // BookingModule.updateBooking(bookingCopy);
      //     - Create payment code accountings of all applied payment codes
      // lets get all applied payment codes and sort them by applyDateTime
      const sortedAppliedPaymentCodes = BookingModule.appliedPromocodes.filter(
        (p) =>
          p.promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER ||
          p.promocode.promoCodeType ===
            PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
          p.promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD ||
          p.promocode.promoCodeType ===
            PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
      ); /*.sort((p) => p.createdAt)*/

      for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
        createPaymentCodeAccounting(bookingCopy, sortedAppliedPaymentCodes[i]);
        // If there is no accounting created for this code (may be booking is already full discounted) then lets remove it from BookingModule.promocodes[]
        // If there is no accounting created for this code (may be booking is already full discounted) then lets remove it from BookingModule.promocodes[]
        const stillValidAccountings = bookingCopy.accountings.filter(
          (acc) =>
            (acc.promoCodeUuid ===
              sortedAppliedPaymentCodes[i].promocode.code ||
              acc.promocode === sortedAppliedPaymentCodes[i].promocode.code) &&
            (acc.state === AccountingState.Pending ||
              acc.state === AccountingState.Completed)
        );

        if (stillValidAccountings && stillValidAccountings.length > 0) {
          const accEntries = stillValidAccountings
            .map((acc) => acc.accountingEntriesList)
            .flat();
          if (accEntries && accEntries.length > 0) {
            // So we have a valid accounting for this code, lets not remove it
            continue;
          } else {
            // So we have no valid accounting for this code, lets remove it from BookingModule.promocodes
            BookingModule.removePromocode({
              bookingUuid: String(bookingCopy.uuid),
              promocodeUuid: sortedAppliedPaymentCodes[i].promocode.code,
            });
          }
        } else {
          // So we have no valid accounting for this code, lets remove it from BookingModule.promocodes
          BookingModule.removePromocode({
            bookingUuid: String(bookingCopy.uuid),
            promocodeUuid: sortedAppliedPaymentCodes[i].promocode.code,
          });
        }
      }
      BookingModule.updateBooking(bookingCopy);

      // Now lets update bookingCurrentAmount
      BookingModule.updateCurrentAmount({
        BookingUUID: BookingModule.selectedBookingUUID,
        amount: restToPay.value / 100,
      });
      BookingModule.setSplitPaymentAmount({
        BookingUUID: BookingModule.selectedBookingUUID,
        splitAmount: restToPay.value / 100,
      });
      if (restToPay.value > 0) {
        BookingModule.setBookingStep({
          bookingUUID: BookingModule.selectedBookingUUID,
          step: BookingStep.Payment,
        });
      }

      AppModule.updatedIsLoading(false);
    }
    const canceledOrAbandonedPssSum = computed(() => {
      const bookingCopy = cloneDeep(BookingModule.selectedBooking);
      const canceledPssList =
        bookingCopy?.pssList
          .filter(
            (pss) =>
              pss.bookingStatus === BookingStatus.Canceled ||
              pss.bookingStatus === BookingStatus.Abandoned
          )
          .map((pss) => getPSSValue(pss)) || [];
      return sumArray(canceledPssList);
    });
    const total = computed(() => {
      return !discountResult.value
        ? (
            (BookingModule.pssSum - canceledOrAbandonedPssSum.value) /
            100
          ).toFixed(2)
        : (
            (BookingModule.pssSum -
              canceledOrAbandonedPssSum.value -
              discountResult.value) /
            100
          ).toFixed(2);
    });

    return {
      calcIcon,
      calcAlt,
      calcIconStyle,
      predefinedDiscounts,
      discountInfo,
      DiscountTypes,
      discountResult,
      // discountResult1,
      isDiscountApplied,
      currency,
      showCalculator,
      updateDiscountType,
      updateDiscountValue,
      UpdateDiscountApplicationCoverage,
      makeDiscountOnAllOrder,
      // applyDiscount,
      applyDiscount2,
      refreshDiscount,
      total,
    };
  },
  created() {
    // If the user refreses the page we will loose the discount he created, and the discount component will need a new one to show some options selected
    const d = BookingModule.selectedBookingDiscountTrack;
    if (d) {
      return;
    }
    // Since no discount or discount lost, lets create a new empty one
    initiateDiscount();
  },
});
</script>

<style lang="scss" scoped>
.discount-type-btn {
  height: 52px;
  font-size: 14px;
  font-weight: 500;
}

.discount-value-btn {
  height: 52px;
  width: 100%;
}

.discount-quantity-btn {
  font-size: 16px;
  font-weight: 500;
}
</style>
